/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ScoreCardResultsService {

    /**
     * Read the fill-out context and the current evaluator's result
     * @param templateId
     * @param applicantId applicant external id
     * @returns any the template, the applicant context, and this evaluator's draft or submission (null before the first save)
     * @throws ApiError
     */
    public static getInternalApiScoreCardsTemplatesApplicantsResult(
        templateId: number,
        applicantId: string,
    ): CancelablePromise<{
        score_card?: {
            template?: {
                id?: number;
                name?: string;
                template_type?: 'unweighted' | 'weighted' | 'text';
                questions?: Array<{
                    id?: number;
                    text?: string;
                    description?: string | null;
                    /**
                     * recommendation is reserved: exactly one exists and it is always last
                     */
                    question_type?: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation';
                    required?: boolean;
                    /**
                     * always false for free_text and recommendation
                     */
                    allow_notes?: boolean;
                    /**
                     * 1-5 is the only rating scale
                     */
                    rating_max?: 5;
                    /**
                     * dropdown choices; the recommendation option keys (strong_no, no, yes, strong_yes); empty for every other type
                     */
                    options?: Array<string>;
                    /**
                     * free_text only — the hint the evaluator sees
                     */
                    hint_text?: string | null;
                    /**
                     * percent of the template total; 0 unless the scorecard is weighted
                     */
                    weight?: number;
                    /**
                     * zero-based display order
                     */
                    position?: number | null;
                }>;
            };
            /**
             * Contact fields and the resume appear only for a user with real applicant access; an invited evaluator without it gets id and name alone.
             */
            applicant?: {
                /**
                 * the applicant external id
                 */
                id?: string;
                name?: string;
                email?: string | null;
                phone_number?: string | null;
                resume?: {
                    title?: string;
                    url?: string;
                } | null;
            };
            opening_title?: string | null;
            /**
             * the stage this scorecard instance sits on; type and short_type drive the stage icon
             */
            stage?: {
                title?: string | null;
                type?: string;
                short_type?: string | null;
            } | null;
            result?: {
                id?: number;
                /**
                 * a submitted result refuses every further write
                 */
                status?: 'draft' | 'submitted';
                score?: number;
                submitted_at?: string | null;
                answers?: Array<{
                    /**
                     * null once the question is deleted; the question snapshot still describes it
                     */
                    question_id?: number | null;
                    /**
                     * rating questions only, 1..rating_max
                     */
                    score?: number | null;
                    /**
                     * the answer for every other type: yes/no, a dropdown choice, free text, or a recommendation key
                     */
                    value?: string | null;
                    note?: string | null;
                    /**
                     * the question as the evaluator answered it — the submission snapshot, or the live question for rows from before snapshotting; null when that question is gone too
                     */
                    question?: {
                        /**
                         * the question id, stable across edits and deletion; null only for a pre-id snapshot of a since-deleted question
                         */
                        id?: number | null;
                        text?: string | null;
                        question_type?: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation' | null;
                        /**
                         * zero-based display order at submission time
                         */
                        position?: number | null;
                    } | null;
                }>;
            } | null;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/score_cards/templates/{template_id}/applicants/{applicant_id}/result',
            path: {
                'template_id': templateId,
                'applicant_id': applicantId,
            },
            errors: {
                403: `the score-card-templates flag is off for this account`,
                404: `not found`,
            },
        });
    }

    /**
     * Autosave the current evaluator's draft answers
     * @param templateId
     * @param applicantId applicant external id
     * @param requestBody
     * @returns any the draft after the save
     * @throws ApiError
     */
    public static patchInternalApiScoreCardsTemplatesApplicantsResult(
        templateId: number,
        applicantId: string,
        requestBody: {
            score_card_result: {
                /**
                 * Upserted by question_id; a blank score or value clears that slot, so a deselected yes/no stays unanswered.
                 */
                answers?: Array<{
                    question_id: number;
                    score?: number | null;
                    value?: string | null;
                    note?: string | null;
                }>;
            };
        },
    ): CancelablePromise<{
        score_card_result?: {
            id?: number;
            /**
             * a submitted result refuses every further write
             */
            status?: 'draft' | 'submitted';
            score?: number;
            submitted_at?: string | null;
            answers?: Array<{
                /**
                 * null once the question is deleted; the question snapshot still describes it
                 */
                question_id?: number | null;
                /**
                 * rating questions only, 1..rating_max
                 */
                score?: number | null;
                /**
                 * the answer for every other type: yes/no, a dropdown choice, free text, or a recommendation key
                 */
                value?: string | null;
                note?: string | null;
                /**
                 * the question as the evaluator answered it — the submission snapshot, or the live question for rows from before snapshotting; null when that question is gone too
                 */
                question?: {
                    /**
                     * the question id, stable across edits and deletion; null only for a pre-id snapshot of a since-deleted question
                     */
                    id?: number | null;
                    text?: string | null;
                    question_type?: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation' | null;
                    /**
                     * zero-based display order at submission time
                     */
                    position?: number | null;
                } | null;
            }>;
        } | null;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/score_cards/templates/{template_id}/applicants/{applicant_id}/result',
            path: {
                'template_id': templateId,
                'applicant_id': applicantId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `the scorecard was already submitted`,
            },
        });
    }

    /**
     * Read every evaluator's result side by side, for the results modal
     * @param templateId
     * @param applicantId applicant external id
     * @returns any the template, the applicant, and one evaluation per evaluator — a pending evaluator carries no answers, score, or recommendation
     * @throws ApiError
     */
    public static getInternalApiScoreCardsTemplatesApplicantsResults(
        templateId: number,
        applicantId: string,
    ): CancelablePromise<{
        score_card_results?: {
            template?: {
                id?: number;
                name?: string;
                template_type?: 'unweighted' | 'weighted' | 'text';
                questions?: Array<{
                    id?: number;
                    text?: string;
                    description?: string | null;
                    /**
                     * recommendation is reserved: exactly one exists and it is always last
                     */
                    question_type?: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation';
                    required?: boolean;
                    /**
                     * always false for free_text and recommendation
                     */
                    allow_notes?: boolean;
                    /**
                     * 1-5 is the only rating scale
                     */
                    rating_max?: 5;
                    /**
                     * dropdown choices; the recommendation option keys (strong_no, no, yes, strong_yes); empty for every other type
                     */
                    options?: Array<string>;
                    /**
                     * free_text only — the hint the evaluator sees
                     */
                    hint_text?: string | null;
                    /**
                     * percent of the template total; 0 unless the scorecard is weighted
                     */
                    weight?: number;
                    /**
                     * zero-based display order
                     */
                    position?: number | null;
                }>;
            };
            applicant?: {
                /**
                 * the applicant external id
                 */
                id?: string;
                name?: string;
            };
            /**
             * one entry per evaluator holding a result row, in assignment order
             */
            evaluations?: Array<{
                user?: {
                    /**
                     * the evaluator user external id
                     */
                    id?: string | null;
                    name?: string | null;
                };
                /**
                 * true on the signed-in evaluator's own column
                 */
                current_user?: boolean;
                /**
                 * pending covers an evaluator with a draft — their answers are never serialized
                 */
                status?: 'submitted' | 'pending';
                submitted_at?: string | null;
                /**
                 * pending only — the booked slot on a scheduler stage, the stage landing otherwise
                 */
                invited_at?: string | null;
                /**
                 * the mode the evaluator submitted under, from the answer snapshots; null while pending
                 */
                submitted_template_type?: 'unweighted' | 'weighted' | 'text' | null;
                /**
                 * the stored submission score; null while pending, and on a submission made under a comments-only template
                 */
                score?: number | null;
                /**
                 * the submitted recommendation key, from the answer snapshot
                 */
                recommendation?: 'strong_no' | 'no' | 'yes' | 'strong_yes' | null;
                /**
                 * empty while pending
                 */
                answers?: Array<{
                    /**
                     * null once the question is deleted; the question snapshot still describes it
                     */
                    question_id?: number | null;
                    /**
                     * rating questions only, 1..rating_max
                     */
                    score?: number | null;
                    /**
                     * the answer for every other type: yes/no, a dropdown choice, free text, or a recommendation key
                     */
                    value?: string | null;
                    note?: string | null;
                    /**
                     * the question as the evaluator answered it — the submission snapshot, or the live question for rows from before snapshotting; null when that question is gone too
                     */
                    question?: {
                        /**
                         * the question id, stable across edits and deletion; null only for a pre-id snapshot of a since-deleted question
                         */
                        id?: number | null;
                        text?: string | null;
                        question_type?: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation' | null;
                        /**
                         * zero-based display order at submission time
                         */
                        position?: number | null;
                    } | null;
                }>;
            }>;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/score_cards/templates/{template_id}/applicants/{applicant_id}/results',
            path: {
                'template_id': templateId,
                'applicant_id': applicantId,
            },
            errors: {
                403: `the score-card-templates flag is off for this account`,
            },
        });
    }

    /**
     * Submit the scorecard — one-way, snapshots every answered question's wording
     * @param templateId
     * @param applicantId applicant external id
     * @param requestBody
     * @returns any submitted
     * @throws ApiError
     */
    public static postInternalApiScoreCardsTemplatesApplicantsResultSubmit(
        templateId: number,
        applicantId: string,
        requestBody: {
            score_card_result: {
                /**
                 * Upserted by question_id; a blank score or value clears that slot, so a deselected yes/no stays unanswered.
                 */
                answers?: Array<{
                    question_id: number;
                    score?: number | null;
                    value?: string | null;
                    note?: string | null;
                }>;
            };
        },
    ): CancelablePromise<{
        score_card_result?: {
            id?: number;
            /**
             * a submitted result refuses every further write
             */
            status?: 'draft' | 'submitted';
            score?: number;
            submitted_at?: string | null;
            answers?: Array<{
                /**
                 * null once the question is deleted; the question snapshot still describes it
                 */
                question_id?: number | null;
                /**
                 * rating questions only, 1..rating_max
                 */
                score?: number | null;
                /**
                 * the answer for every other type: yes/no, a dropdown choice, free text, or a recommendation key
                 */
                value?: string | null;
                note?: string | null;
                /**
                 * the question as the evaluator answered it — the submission snapshot, or the live question for rows from before snapshotting; null when that question is gone too
                 */
                question?: {
                    /**
                     * the question id, stable across edits and deletion; null only for a pre-id snapshot of a since-deleted question
                     */
                    id?: number | null;
                    text?: string | null;
                    question_type?: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation' | null;
                    /**
                     * zero-based display order at submission time
                     */
                    position?: number | null;
                } | null;
            }>;
        } | null;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/score_cards/templates/{template_id}/applicants/{applicant_id}/result/submit',
            path: {
                'template_id': templateId,
                'applicant_id': applicantId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `required questions are unanswered`,
            },
        });
    }

}